import { v4 as uuidv4 } from 'uuid';

export function updateAction(id, properties, actions) {
    return actions.map(action => {
        if (action.id === id) {
            return {
                ...action,
                ...properties
            };
        }
        if (action.actions && action.actions.length > 0) {
            action.actions = updateAction(id, properties, action.actions);
        }
        return action;
    })
}

export function extractLayoutPropertiesForDB(layout) {
    return layout.map(section => extractSectionPropertiesForDB(section));
}

export function extraStepsPropertiesForDB(steps) {
    for (const step of steps) {
        const layout = step.layout;
        let mapLayout = extractLayoutPropertiesForDB(layout);
        step.layout = mapLayout;
    }
    return steps;
}

export function addColumn(layout, updatedColumns, dropSectionId, columnToAdd, componentType) {
    const droppingColumn = updatedColumns.find(item => item.i === "__dropping-elem__");
    const updatedLayout = layout.map(section => {
        if (section.id === dropSectionId) {
            const updatedColumnsInSection = section.columns.map(column => {
                const updatedColumn = updatedColumns.find(item => item.i === column.i);
                return {
                    ...column,
                    x: updatedColumn.x,
                    y: updatedColumn.y
                };
            });

            const newColumn = createColumn(columnToAdd, section.columns.length, {
                x: droppingColumn.x,
                y: droppingColumn.y
            }, componentType);

            return {
                ...section,
                columns: [...updatedColumnsInSection, newColumn]
            };
        }
        return section;
    });
    return updatedLayout;
}

export function removeColumn(layout, attributeId) {
    const updatedLayout = layout.map((section) => {
        section.columns = section.columns.filter(column => column.attributeId !== attributeId);
        return section;
    });
    return updatedLayout;
}

export function toggleColumnHyperlink(layout, attributeId, hyperlink, targetPageId) {
    const updatedLayout = layout.map((section) => {
        section.columns = section.columns.map(column => {
            if (column.attributeId === attributeId) {
                return {
                    ...column,
                    hyperlink: hyperlink,
                    targetPageId: targetPageId
                }
            }
            return column;
        });
        return section;
    });
    return updatedLayout;
}

export function toggleColumnColorFormatting(layout, attributeId, cellRules) {
    const updatedLayout = layout.map((section) => {
        section.columns = section.columns.map(column => {
            if (column.attributeId === attributeId) {
                return {
                    ...column,
                    cellRules: cellRules
                }
            }
            return column;
        });
        return section;
    });
    return updatedLayout;
}

export function updateColumnSettings(layout, attributeId, columnSettings) {
    const updatedLayout = layout.map((section) => {
        section.columns = section.columns.map(column => {
            if (column.attributeId === attributeId) {
                return {
                    ...column,
                    columnSettings: columnSettings
                }
            }
            return column;
        });
        return section;
    });
    return updatedLayout;
}

export function dropColumnInDifferentSection(layout, updatedColumns, dragSectionId, dropSectionId, draggedColumn) {
    const droppingColumn = updatedColumns.find(item => item.i === "__dropping-elem__");
    const updatedLayout = layout.map(section => {
        if (section.id === dragSectionId) {
            return {
                ...section,
                columns: section.columns.filter(column => column.i !== draggedColumn.i)
            }
        }
        if (section.id === dropSectionId) {
            return {
                ...section,
                columns: [
                    ...section.columns.map(column => ({
                        ...column,
                        x: updatedColumns.find(item => item.i === column.i).x,
                        y: updatedColumns.find(item => item.i === column.i).y
                    })),
                    {
                        ...draggedColumn,
                        x: droppingColumn.x,
                        y: droppingColumn.y
                    }
                ]
            }
        }
        return section
    });
    return updatedLayout;
}

export function dropColumnInSameSection(layout, updatedColumns, dropSectionId, draggedColumn) {
    updatedColumns = arrangeColumns(updatedColumns, draggedColumn.i);
    const updatedLayout = layout.map((section) => {
        if (section.id === dropSectionId) {
            return {
                ...section,
                columns: section.columns.map(column => ({
                    ...column,
                    ...updatedColumns.find(item => item.i === column.i),
                }))
            };
        }
        return section;
    });
    return updatedLayout;
}

export function resizeColumns(layout, sectionId, updatedColumns) {
    const updatedLayout = layout.map((section) => {
        if (section.id === sectionId) {
            return {
                ...section,
                columns: section.columns.map(column => ({
                    ...column,
                    ...updatedColumns.find(item => item.i === column.i),
                }))
            };
        }
        return section;
    });
    return updatedLayout;
}

export function moveSectionUp(layout, sectionId) {
    const sectionIndex = layout.findIndex(section => section.id === sectionId);
    if (sectionIndex > 0) {
        const section = layout[sectionIndex];
        layout[sectionIndex] = layout[sectionIndex - 1];
        layout[sectionIndex - 1] = section;
    }
    return layout;
}

export function moveSectionDown(layout, sectionId) {
    const sectionIndex = layout.findIndex(section => section.id === sectionId);
    if (sectionIndex < layout.length - 1) {
        const section = layout[sectionIndex];
        layout[sectionIndex] = layout[sectionIndex + 1];
        layout[sectionIndex + 1] = section;
    }
    return layout;
}

function arrangeColumns(columns, droppingColumnId) {
    const droppingColum = columns.find(item => item.i === "__dropping-elem__");
    const updatedColumns = columns.map(item => {
        if (item.i === droppingColumnId) {
            return {
                ...item,
                x: droppingColum.x,
                y: droppingColum.y
            }
        }
        return item;
    }).filter(item => item.i !== "__dropping-elem__");

    return updatedColumns;
}

function createColumn(column, index, position = {}, componentType) {
    let segmentColor = [];
    if (column.attributeType === "Picklist String" && column.picklist) {
        // Split picklist string by 'ɖ' separator and create color entries for each value
        const picklistValues = column.picklist.split('ɖ').filter(value => value.trim() !== '');
        segmentColor = picklistValues.map(() => ({ textColor: "#ffffff", fillColor: "#000000" }));
    }

    const updatedColumn = {
        i: uuidv4(),
        x: index % 2 === 0 ? 0 : 6,
        y: Math.floor(index / 2),
        w: generateColumnWidth(column.attributeType, componentType),
        h: generateColumnHeight(column.attributeType),
        minW: generateColumnMinWidth(column.attributeType),
        maxW: 12,
        minH: generateColumnHeight(column.attributeType),
        attributeId: column._id,
        columnSettings: {
            displayType: column.attributeType === "Picklist String" ? "SegmentedControl" : "Inherit",
            fontSize: 14,
            fontWeight: "regular",
            placeholder: "",
            segmentColor: segmentColor,
            cellRules: column.attributeType === "Picklist String" ? true : false,
            showLabel: componentType === "Cards" ? true : false,
            hyperlink: false,
            targetPageId: ""
        },
        ...position
    };
    return updatedColumn;
}

function generateColumnMinWidth(type) {
    switch (type) {
        case "Multiline Text":
            return 6;
        case "Attachment":
            return 6;
        case "Html":
            return 6;
        default:
            return 1;
    }
}

function generateColumnHeight(type) {
    switch (type) {
        case "Multiline Text":
            return 2
        case "Attachment":
            return 2;
        case "Html":
            return 3;
        default:
            return 1;
    }
}

function generateColumnWidth(type, componentType = "Form") {
    // For Cards component, always return width 12
    if (componentType === "Cards") {
        return 12;
    }

    // For Form and View components, use the existing logic
    switch (type) {
        case "Multiline Text":
            return 6;
        case "Attachment":
            return 6;
        case "Html":
            return 12;
        default:
            return 6;
    }
}

function extractSectionPropertiesForDB(section) {
    return {
        id: section.id,
        section: section.section || "",
        columns: section.columns.map(column => extractColumnPropertiesForDB(column)) || [],
        languages: section.languages || {}
    };
}

function extractColumnPropertiesForDB(column) {
    return {
        i: column.i,
        x: column.x,
        y: column.y,
        w: column.w,
        h: column.h,
        minW: column.minW,
        minH: column.minH,
        maxW: column.maxW,
        maxH: column.maxH,
        attributeId: column.attributeId,
        columnSettings: column.columnSettings || {}
    };
}